/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Badge,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Table,
  Container,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Form,
  FormGroup,
  Label,
  Spinner,
  Alert
} from "reactstrap";
import { FaPlus, FaEdit, FaTrash, FaSearch, FaUserShield, FaUserCheck, FaUserTimes } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdminUserManagement = () => {
  // Add custom CSS for modal headers
  const modalHeaderStyle = {
    backgroundColor: '#2d2d2d',
    color: '#ffffff',
    borderBottom: '1px solid #404040',
    fontWeight: 'bold'
  };

  const modalTitleStyle = {
    color: '#ffffff',
    margin: 0,
    fontWeight: 'bold',
    fontSize: '1.25rem'
  };
  // Check if user is super admin
  const isSuperAdmin = () => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const user = JSON.parse(userData);
        return user.isSuperAdmin === true;
      }
      return false;
    } catch (error) {
      console.error('Error checking super admin status:', error);
      return false;
    }
  };

  // Redirect if not super admin
  if (!isSuperAdmin()) {
    return (
      <div className="header bg-gradient-success pb-8 pt-5 pt-md-8" style={{ backgroundColor: '#1a1a1a' }}>
        <Container fluid>
          <Row>
            <div className="col">
              <div className="card shadow" style={{ backgroundColor: '#2d2d2d', border: '1px solid #404040' }}>
                <div className="card-body text-center" style={{ color: '#ffffff' }}>
                  <h3 className="text-danger">Access Denied</h3>
                  <p style={{ color: '#cccccc' }}>You don't have permission to access this page.</p>
                  <p style={{ color: '#cccccc' }}>Only super admin users can manage admin users.</p>
                </div>
              </div>
            </div>
          </Row>
        </Container>
      </div>
    );
  }

  // State variables
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  });

  // Form states
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [createForm, setCreateForm] = useState({
    name: "",
    email: "",
    password: "",
    phoneNumber: ""
  });

  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    isblocked: false
  });

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Auth fetch function
  const authFetch = async (url, options = {}) => {
    const token = getToken();
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const response = await fetch(url, { ...options, headers });
    
    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem('token');
      window.location.href = '/auth/login';
      throw new Error('Authentication failed');
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  };

  // Fetch admin users
  const fetchAdminUsers = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20",
        ...(search && { search })
      });

      const data = await authFetch(`${process.env.REACT_APP_API_URL}/admin-users?${params}`);
      setAdminUsers(data.adminUsers);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Error fetching admin users:', error);
      toast.error(error.message || 'Failed to fetch admin users');
    } finally {
      setLoading(false);
    }
  };

  // Create admin user
  const createAdminUser = async () => {
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/admin-users`, {
        method: 'POST',
        body: JSON.stringify(createForm)
      });

      toast.success('Admin user created successfully');
      setCreateModal(false);
      setCreateForm({ name: "", email: "", password: "", phoneNumber: "" });
      fetchAdminUsers(pagination.page, searchQuery);
    } catch (error) {
      console.error('Error creating admin user:', error);
      toast.error(error.message || 'Failed to create admin user');
    }
  };

  // Update admin user
  const updateAdminUser = async () => {
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/admin-users/${selectedAdmin._id}`, {
        method: 'PUT',
        body: JSON.stringify(editForm)
      });

      toast.success('Admin user updated successfully');
      setEditModal(false);
      setSelectedAdmin(null);
      fetchAdminUsers(pagination.page, searchQuery);
    } catch (error) {
      console.error('Error updating admin user:', error);
      toast.error(error.message || 'Failed to update admin user');
    }
  };

  // Delete admin user
  const deleteAdminUser = async () => {
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/admin-users/${selectedAdmin._id}`, {
        method: 'DELETE'
      });

      toast.success('Admin user deleted successfully');
      setDeleteModal(false);
      setSelectedAdmin(null);
      fetchAdminUsers(pagination.page, searchQuery);
    } catch (error) {
      console.error('Error deleting admin user:', error);
      toast.error(error.message || 'Failed to delete admin user');
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchAdminUsers(1, searchQuery);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    fetchAdminUsers(newPage, searchQuery);
  };

  // Open edit modal
  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setEditForm({
      name: admin.name || "",
      email: admin.email || "",
      phoneNumber: admin.phoneNumber || "",
      isblocked: admin.isblocked || false
    });
    setEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (admin) => {
    setSelectedAdmin(admin);
    setDeleteModal(true);
  };

  // Load admin users on component mount
  useEffect(() => {
    fetchAdminUsers();
  }, []);

  return (
    <>
      <style>
        {`
          .modal-header {
            color: #ffffff !important;
          }
          .modal-header h4,
          .modal-header h5,
          .modal-header h6,
          .modal-header span {
            color: #ffffff !important;
          }
          .modal-header .close {
            color: #ffffff !important;
            opacity: 0.8;
          }
          .modal-header .close:hover {
            color: #ffffff !important;
            opacity: 1;
          }
          .badge {
            color: #ffffff !important;
          }
          .badge-success,
          .badge-danger,
          .badge-warning,
          .badge-info {
            color: #ffffff !important;
          }
        `}
      </style>
      <div className="header pb-8 pt-5 pt-md-8" style={{ backgroundColor: '#1a1a1a' }}>
        <Container fluid>
          <div className="header-body">
            <Row>
              <div className="col">
                <div className="card shadow" style={{ backgroundColor: '#2d2d2d', border: '1px solid #404040' }}>
                  <div className="card-header border-0" style={{ backgroundColor: '#2d2d2d', borderBottom: '1px solid #404040' }}>
                    <Row className="align-items-center">
                      <div className="col">
                        <h3 className="mb-0" style={{ color: '#ffffff' }}>
                          <FaUserShield className="mr-2" style={{ color: '#ffa800' }} />
                          Admin User Management
                        </h3>
                        <p className="mb-0" style={{ color: '#cccccc' }}>Manage admin users and permissions</p>
                      </div>
                      <div className="col text-right">
                        <Button
                          style={{ backgroundColor: '#ffa800', borderColor: '#ffa800' }}
                          onClick={() => setCreateModal(true)}
                          className="btn-sm"
                        >
                          <FaPlus className="mr-1" />
                          Add Admin User
                        </Button>
                      </div>
                    </Row>
                  </div>
                </div>
              </div>
            </Row>
          </div>
        </Container>
      </div>

      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow" style={{ backgroundColor: '#2d2d2d', border: '1px solid #404040' }}>
              <CardHeader className="border-0" style={{ backgroundColor: '#2d2d2d', borderBottom: '1px solid #404040' }}>
                <Row className="align-items-center">
                  <div className="col">
                    <h3 className="mb-0" style={{ color: '#ffffff' }}>Admin Users</h3>
                  </div>
                  <div className="col text-right">
                    <Form onSubmit={handleSearch} className="d-flex">
                      <Input
                        type="text"
                        placeholder="Search admin users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="mr-2"
                        style={{ backgroundColor: '#404040', border: '1px solid #555', color: '#ffffff' }}
                      />
                      <Button type="submit" size="sm" style={{ backgroundColor: '#ffa800', borderColor: '#ffa800' }}>
                        <FaSearch />
                      </Button>
                    </Form>
                  </div>
                </Row>
              </CardHeader>

              <CardBody style={{ backgroundColor: '#2d2d2d' }}>
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner style={{ color: '#28a745' }} />
                    <p className="mt-2" style={{ color: '#ffffff' }}>Loading admin users...</p>
                  </div>
                ) : (
                  <>
                    <Table className="align-items-center table-flush" responsive style={{ backgroundColor: '#2d2d2d' }}>
                      <thead style={{ backgroundColor: '#404040' }}>
                        <tr>
                          <th scope="col" style={{ color: '#ffffff', borderBottom: '1px solid #555' }}>Name</th>
                          <th scope="col" style={{ color: '#ffffff', borderBottom: '1px solid #555' }}>Email</th>
                          <th scope="col" style={{ color: '#ffffff', borderBottom: '1px solid #555' }}>Phone</th>
                          <th scope="col" style={{ color: '#ffffff', borderBottom: '1px solid #555' }}>Wallet</th>
                          <th scope="col" style={{ color: '#ffffff', borderBottom: '1px solid #555' }}>Status</th>
                          <th scope="col" style={{ color: '#ffffff', borderBottom: '1px solid #555' }}>Type</th>
                          <th scope="col" style={{ color: '#ffffff', borderBottom: '1px solid #555' }}>Created</th>
                          <th scope="col" style={{ color: '#ffffff', borderBottom: '1px solid #555' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminUsers.map((admin) => (
                          <tr key={admin._id} style={{ borderBottom: '1px solid #404040' }}>
                            <td style={{ color: '#ffffff' }}>
                              <span className="font-weight-bold">
                                {admin.name || 'N/A'}
                              </span>
                            </td>
                            <td style={{ color: '#ffffff' }}>{admin.email || 'N/A'}</td>
                            <td style={{ color: '#ffffff' }}>{admin.phoneNumber || 'N/A'}</td>
                            <td style={{ color: '#ffffff' }}>
                              {admin.wallet ? (
                                <span className="text-truncate d-inline-block" style={{ maxWidth: '150px' }}>
                                  {admin.wallet}
                                </span>
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td>
                              <Badge color={admin.isblocked ? "danger" : "success"} style={{ color: '#ffffff' }}>
                                {admin.isblocked ? (
                                  <>
                                    <FaUserTimes className="mr-1" />
                                    Blocked
                                  </>
                                ) : (
                                  <>
                                    <FaUserCheck className="mr-1" />
                                    Active
                                  </>
                                )}
                              </Badge>
                            </td>
                            <td>
                              <Badge color={admin.isSuperAdmin ? "warning" : "info"} style={{ color: '#ffffff' }}>
                                {admin.isSuperAdmin ? "Super Admin" : "Admin"}
                              </Badge>
                            </td>
                            <td style={{ color: '#ffffff' }}>
                              {new Date(admin.createdAt).toLocaleDateString()}
                            </td>
                            <td>
                              <div className="d-flex">
                                <Button
                                  size="sm"
                                  className="mr-2"
                                  onClick={() => openEditModal(admin)}
                                  disabled={admin.isSuperAdmin}
                                  style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8' }}
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => openDeleteModal(admin)}
                                  disabled={admin.isSuperAdmin}
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {adminUsers.length === 0 && (
                      <div className="text-center py-4">
                        <p style={{ color: '#cccccc' }}>No admin users found</p>
                      </div>
                    )}
                  </>
                )}
              </CardBody>

              {pagination.totalPages > 1 && (
                <CardFooter className="py-4" style={{ backgroundColor: '#2d2d2d', borderTop: '1px solid #404040' }}>
                  <nav aria-label="...">
                    <ul className="pagination justify-content-end mb-0">
                      <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                        <Button
                          className="page-link"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page === 1}
                          style={{ backgroundColor: '#404040', border: '1px solid #555', color: '#ffffff' }}
                        >
                          <i className="fas fa-angle-left" />
                          <span className="sr-only">Previous</span>
                        </Button>
                      </li>
                      {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                        <li key={page} className={`page-item ${page === pagination.page ? 'active' : ''}`}>
                          <Button
                            className="page-link"
                            onClick={() => handlePageChange(page)}
                            style={{ 
                              backgroundColor: page === pagination.page ? '#28a745' : '#404040', 
                              border: '1px solid #555', 
                              color: '#ffffff' 
                            }}
                          >
                            {page}
                          </Button>
                        </li>
                      ))}
                      <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                        <Button
                          className="page-link"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page === pagination.totalPages}
                          style={{ backgroundColor: '#404040', border: '1px solid #555', color: '#ffffff' }}
                        >
                          <i className="fas fa-angle-right" />
                          <span className="sr-only">Next</span>
                        </Button>
                      </li>
                    </ul>
                  </nav>
                </CardFooter>
              )}
            </Card>
          </div>
        </Row>
      </Container>

      {/* Create Admin User Modal */}
      <Modal isOpen={createModal} toggle={() => setCreateModal(false)} size="lg">
        <ModalHeader toggle={() => setCreateModal(false)} style={modalHeaderStyle}>
          <span style={modalTitleStyle}>Create New Admin User</span>
        </ModalHeader>
        <ModalBody style={{ backgroundColor: '#2d2d2d' }}>
          <Form>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="createName" style={{ color: '#ffffff' }}>Name</Label>
                  <Input
                    type="text"
                    id="createName"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="Enter name"
                    style={{ backgroundColor: '#404040', border: '1px solid #555', color: '#ffffff' }}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="createEmail" style={{ color: '#ffffff' }}>Email *</Label>
                  <Input
                    type="email"
                    id="createEmail"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    placeholder="Enter email"
                    required
                    style={{ backgroundColor: '#404040', border: '1px solid #555', color: '#ffffff' }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="createPassword" style={{ color: '#ffffff' }}>Password *</Label>
                  <Input
                    type="password"
                    id="createPassword"
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    placeholder="Enter password"
                    required
                    style={{ backgroundColor: '#404040', border: '1px solid #555', color: '#ffffff' }}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="createPhone" style={{ color: '#ffffff' }}>Phone Number</Label>
                  <Input
                    type="tel"
                    id="createPhone"
                    value={createForm.phoneNumber}
                    onChange={(e) => setCreateForm({ ...createForm, phoneNumber: e.target.value })}
                    placeholder="Enter phone number"
                    style={{ backgroundColor: '#404040', border: '1px solid #555', color: '#ffffff' }}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter style={{ backgroundColor: '#2d2d2d', borderTop: '1px solid #404040' }}>
          <Button onClick={() => setCreateModal(false)} style={{ backgroundColor: '#6c757d', borderColor: '#6c757d' }}>
            Cancel
          </Button>
          <Button onClick={createAdminUser} style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}>
            Create Admin User
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Admin User Modal */}
      <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="lg">
        <ModalHeader toggle={() => setEditModal(false)} style={modalHeaderStyle}>
          <span style={modalTitleStyle}>Edit Admin User</span>
        </ModalHeader>
        <ModalBody style={{ backgroundColor: '#2d2d2d' }}>
          <Form>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="editName" style={{ color: '#ffffff' }}>Name</Label>
                  <Input
                    type="text"
                    id="editName"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="Enter name"
                    style={{ backgroundColor: '#404040', border: '1px solid #555', color: '#ffffff' }}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="editEmail" style={{ color: '#ffffff' }}>Email</Label>
                  <Input
                    type="email"
                    id="editEmail"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    placeholder="Enter email"
                    style={{ backgroundColor: '#404040', border: '1px solid #555', color: '#ffffff' }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="editPhone" style={{ color: '#ffffff' }}>Phone Number</Label>
                  <Input
                    type="tel"
                    id="editPhone"
                    value={editForm.phoneNumber}
                    onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                    placeholder="Enter phone number"
                    style={{ backgroundColor: '#404040', border: '1px solid #555', color: '#ffffff' }}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup check>
                  <Input
                    type="checkbox"
                    id="editBlocked"
                    checked={editForm.isblocked}
                    onChange={(e) => setEditForm({ ...editForm, isblocked: e.target.checked })}
                    style={{ backgroundColor: '#404040', border: '1px solid #555' }}
                  />
                  <Label check for="editBlocked" style={{ color: '#ffffff' }}>
                    Block this admin user
                  </Label>
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter style={{ backgroundColor: '#2d2d2d', borderTop: '1px solid #404040' }}>
          <Button onClick={() => setEditModal(false)} style={{ backgroundColor: '#6c757d', borderColor: '#6c757d' }}>
            Cancel
          </Button>
          <Button onClick={updateAdminUser} style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}>
            Update Admin User
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)} style={modalHeaderStyle}>
          <span style={modalTitleStyle}>Confirm Delete</span>
        </ModalHeader>
        <ModalBody style={{ backgroundColor: '#2d2d2d' }}>
          <Alert color="warning" style={{ backgroundColor: '#856404', borderColor: '#856404', color: '#ffffff' }}>
            <strong>Warning!</strong> This action cannot be undone. Are you sure you want to delete this admin user?
          </Alert>
          {selectedAdmin && (
            <div style={{ color: '#ffffff' }}>
              <p><strong>Name:</strong> {selectedAdmin.name || 'N/A'}</p>
              <p><strong>Email:</strong> {selectedAdmin.email || 'N/A'}</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{ backgroundColor: '#2d2d2d', borderTop: '1px solid #404040' }}>
          <Button onClick={() => setDeleteModal(false)} style={{ backgroundColor: '#6c757d', borderColor: '#6c757d' }}>
            Cancel
          </Button>
          <Button color="danger" onClick={deleteAdminUser}>
            Delete Admin User
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </>
  );
};

export default AdminUserManagement;
